/**
 * Planeten-Hub. Vollbild-Overlay mit Tabs: Gebäude, Forschung, Flotte.
 * Kamera zoomt auf Planet, emittiert Events für HUD-Layout.
 */
import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { SupabaseClient } from '../network/SupabaseClient';
import type { PlanetInfo } from '../world/PlayerWorldTypes';
import { updateHubCache, type PlanetHubCache } from '../core/HubDataCache';
import type { TabId } from './PlanetMenuDOM';
import {
  createPlanetHubDOM,
  HUB_DELAY_MS,
  HUB_TRANSITION_MS,
  REVEAL_KEYFRAMES,
  HIDE_KEYFRAMES,
} from './PlanetMenuDOM';
import { PlanetMenuBuildings } from './PlanetMenuBuildings';
import { PlanetMenuFleet } from './PlanetMenuFleet';

const PLANET_ICONS: Record<string, string> = {
  terran: '\u{1F30D}', desert: '\u{1F3DC}\uFE0F', ice: '\u{2744}\uFE0F',
  lava: '\u{1F30B}', gas_giant: '\u{1FA90}', ocean: '\u{1F30A}', volcanic: '\u{1F30B}',
};

interface PlanetOpenEvent { planet: PlanetInfo }

export class PlanetMenu implements GameModule {
  readonly name = 'PlanetMenu';

  private _el!: HTMLDivElement;
  private _contentWrap!: HTMLDivElement;
  private _titleEl!: HTMLSpanElement;
  private _tabsEl!: HTMLDivElement;
  private _contentEl!: HTMLDivElement;
  private _getTabStyle!: (id: TabId, active: TabId) => string;
  private _visible = false;
  private _activePlanet: PlanetInfo | null = null;
  private _activeTab: TabId = 'buildings';
  private _events!: import('../core/EventBus').EventBus;
  private _buildings: PlanetMenuBuildings;
  private _fleet: PlanetMenuFleet;
  private _revealTimeout: ReturnType<typeof setTimeout> | null = null;
  private _revealAnim: Animation | null = null;
  private _hideAnim: Animation | null = null;
  private _hubCache: Map<string, PlanetHubCache>;

  constructor(
    supabase: SupabaseClient | null,
    devMode: boolean,
    hubCache: Map<string, PlanetHubCache> = new Map(),
  ) {
    this._hubCache = hubCache;
    this._buildings = new PlanetMenuBuildings(supabase, devMode);
    this._fleet = new PlanetMenuFleet(supabase, devMode);
  }

  init(engine: Engine): void {
    this._events = engine.events;
    const dom = createPlanetHubDOM(
      () => this._hide(),
      (id) => this._switchTab(id),
      (el) => {
        this._buildings.setHeaderEl(el);
        this._fleet.setHeaderEl(el);
      },
    );
    this._el = dom.el;
    this._contentWrap = dom.contentWrap;
    this._titleEl = dom.titleEl;
    this._tabsEl = dom.tabsEl;
    this._contentEl = dom.contentEl;
    this._getTabStyle = dom.getTabStyle;
    document.body.appendChild(this._el);
    this._events.on<PlanetOpenEvent>('planet:open', (d) => this._show(d.planet));
  }

  update(): void { /* statisch */ }

  dispose(): void { this._el.remove(); }

  private _switchTab(id: TabId): void {
    if (this._activeTab === 'fleet' && id !== 'fleet') this._fleet.stop();
    this._activeTab = id;
    this._updateTabs();
    this._renderContent();
  }

  private _updateTabs(): void {
    const btns = this._tabsEl.querySelectorAll('button[data-tab]');
    for (let i = 0; i < btns.length; i++) {
      const b = btns[i] as HTMLButtonElement;
      b.style.cssText = this._getTabStyle(b.dataset.tab as TabId, this._activeTab);
    }
  }

  private _show(p: PlanetInfo): void {
    this._hideAnim?.cancel();
    this._hideAnim = null;
    if (this._revealTimeout) {
      clearTimeout(this._revealTimeout);
      this._revealTimeout = null;
    }
    this._activePlanet = p;
    this._activeTab = 'buildings';
    const icon = PLANET_ICONS[p.planetType] ?? '\u{1FA90}';
    this._titleEl.textContent = `${icon}  ${p.planetType.toUpperCase()}`;
    this._el.style.display = 'flex';
    this._el.style.clipPath = 'inset(50% 50% 50% 50%)';
    this._visible = true;
    this._events.emit('planet:focus-camera', { axialQ: p.axialQ, axialR: p.axialR });
    this._events.emit('planet:hub-opened', null);
    this._updateTabs();
    this._renderContent();
    this._contentWrap.style.opacity = '0';
    this._revealTimeout = setTimeout(() => {
      this._revealTimeout = null;
      this._revealAnim = this._el.animate(REVEAL_KEYFRAMES, {
        duration: HUB_TRANSITION_MS,
        easing: 'ease-out',
        fill: 'forwards',
      });
      this._revealAnim!.onfinish = () => {
        this._revealAnim = null;
        this._contentWrap.style.opacity = '1';
      };
    }, HUB_DELAY_MS);
  }

  private _renderContent(): void {
    if (!this._activePlanet) return;
    const p = this._activePlanet;
    const cached = this._hubCache.get(p.id);
    const placeholder = (text: string) =>
      `<div style="color:#4a6a7a;padding:40px;text-align:center;font-size:14px;` +
      `letter-spacing:1px">${text}</div>`;
    // Fleet tab needs overflow:hidden for absolute-positioned canvas;
    // other tabs need overflow-y:auto for scrolling.
    if (this._activeTab === 'fleet') {
      this._contentEl.style.overflow = 'hidden';
    } else {
      this._contentEl.style.overflow = '';
      this._contentEl.style.overflowY = 'auto';
    }

    if (this._activeTab === 'buildings') {
      this._buildings.show(
        p,
        this._contentEl,
        cached?.buildings,
        (data) => updateHubCache(this._hubCache, p.id, 'buildings', data),
      );
      return;
    } else if (this._activeTab === 'research') {
      this._contentEl.innerHTML = placeholder('Forschung \u2014 Technologiebaum, Upgrades');
    } else {
      this._fleet.show(
        p,
        this._contentEl,
        cached?.fleet,
        (data) => updateHubCache(this._hubCache, p.id, 'fleet', data),
      );
    }
  }

  private _hide(): void {
    if (!this._visible) return;
    if (this._revealTimeout) {
      clearTimeout(this._revealTimeout);
      this._revealTimeout = null;
    }
    this._revealAnim?.cancel();
    this._revealAnim = null;
    this._visible = false;
    this._contentWrap.style.transition = 'none';
    this._contentWrap.style.opacity = '0';
    this._contentWrap.offsetHeight;
    this._contentWrap.style.transition = '';
    this._buildings.stopTick();
    this._fleet.stop();
    this._events.emit('planet:hub-closed', null);
    this._events.emit('planet:menu-closed', null);
    this._hideAnim = this._el.animate(HIDE_KEYFRAMES, {
      duration: HUB_TRANSITION_MS,
      easing: 'ease-in',
      fill: 'forwards',
    });
    this._hideAnim.onfinish = () => {
      this._hideAnim = null;
      this._el.style.display = 'none';
      this._activePlanet = null;
    };
  }
}
